import React from 'react';

import {assert} from 'Asserts';
import DataConnector from 'connect/DataConnector';
import {Status as AutomationFileStatusGraphQL} from 'graphql-client/__generated/graphqlGlobalTypes';
import waitForEntrySaved from 'lab-automation/utils/waitForEntrySaved';
import {FromServerResultsTable} from 'notebook/integrations/results/ResultsTableConstants';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  LAB_AUTOMATION_PROCESS_OUTPUT_FILE_STARTED_EVENT,
  LAB_AUTOMATION_PROCESS_OUTPUT_FILE_ENDED_EVENT,
} from 'performance/processLabAutomationEvents';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {AutomationOutputFileGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationOutputFileGraphQL';
import {FromServerRunOutputSamplesTable} from '../output-samples/RunOutputSamplesTableConstants';
import {processAutomationOutputFile, setAutomationFileStatus} from './AutomationFileActions';
import {getProcessingSummaryStatistics} from './AutomationFileDAO';
import {runAutomationOutputFileStatusModal} from './AutomationFileStatusModal';
import {
  isOutputFileMissingFolder,
  isOutputFileMissingFolderOrLocation,
  isOutputFileMissingLocation,
} from './isOutputFileMissingFolderOrLocation';

const BASE_TOOLTIP_TEXT = 'Select a ';

interface PendingOrRunningRunAutomationOutputFileButtonProps {
  runId: string | null;
  automationOutputFile: AutomationOutputFileGraphQL;
  isStale: boolean;
  locked: boolean;
  fileId?: string;
  previousFileNames: Set<string>;
  configName: string | null;
  updateAutomationOutputFiles: (automationOutputFiles: Array<AutomationOutputFileGraphQL>) => void;
  handleRefreshVersionedData: () => Promise<void>;
  isRunning?: boolean;
}

function _getTooltipText(automationOutputFile: AutomationOutputFileGraphQL): string | null {
  let missingItem = '';
  if (isOutputFileMissingFolder(automationOutputFile) && !isOutputFileMissingLocation(automationOutputFile)) {
    missingItem = 'folder';
  } else if (
    !isOutputFileMissingFolder(automationOutputFile) &&
    isOutputFileMissingLocation(automationOutputFile)
  ) {
    missingItem = 'location';
  } else if (
    isOutputFileMissingFolder(automationOutputFile) &&
    isOutputFileMissingLocation(automationOutputFile)
  ) {
    missingItem = 'folder and location';
  } else {
    return null;
  }
  return BASE_TOOLTIP_TEXT + missingItem;
}

interface ConnectedProps {
  outputFileStatus: AutomationFileStatusGraphQL | null;
}

class PendingOrRunningRunAutomationOutputFileButtonConnector extends DataConnector<
  PendingOrRunningRunAutomationOutputFileButtonProps,
  ConnectedProps
> {
  enhanceProps(): ConnectedProps {
    return {
      outputFileStatus: this.props.automationOutputFile.id
        ? this.reduxState().ui.automationFileStatus[this.props.automationOutputFile.id]
        : null,
    };
  }
}

interface ProcessAutomationOutputFileButtonProps {
  runId: string | null;
  locked: boolean;
  automationOutputFile: AutomationOutputFileGraphQL;
  isStale: boolean;
  dataTestAttrs: DataTestAttrs;
  updateAutomationOutputFiles: (
    automationOutputFiles: Array<AutomationOutputFileGraphQL>,
    structuredTables?: Array<FromServerRunOutputSamplesTable | FromServerResultsTable>
  ) => void;
  handleRefreshVersionedData: () => Promise<void>;
  fileId?: string;
  setStatusToPending: () => void;
  setStatusToRunning: () => void;
  isRunning?: boolean;
}

function ProcessAutomationOutputFileButton(props: ProcessAutomationOutputFileButtonProps): JSX.Element {
  const processFile = async (): Promise<void> => {
    props.setStatusToRunning();
    try {
      logProfilerTimingEvent(LAB_AUTOMATION_PROCESS_OUTPUT_FILE_STARTED_EVENT);
      const saved = props.fileId != null ? await waitForEntrySaved(props.fileId) : true;
      if (saved) {
        const response = await processAutomationOutputFile(props.automationOutputFile.id, props.fileId);
        logProfilerTimingEvent(LAB_AUTOMATION_PROCESS_OUTPUT_FILE_ENDED_EVENT);
        props.updateAutomationOutputFiles([response!.automationOutputFile], response!.structuredTables);
      }
    } catch (err) {
      props.setStatusToPending();
      throw err;
    }
  };
  return (
    <TooltipWrapper tooltipTitle={_getTooltipText(props.automationOutputFile)}>
      <Button
        className="processAutomationFileButton"
        buttonStyle={ButtonStyle.SECONDARY}
        size={ButtonSize.MINI}
        fullWidth={true}
        text={props.isStale ? 'Data is stale - click to sync run' : 'Process'}
        icon="refresh"
        disabled={
          props.locked || (!props.isStale && isOutputFileMissingFolderOrLocation(props.automationOutputFile))
        }
        onClick={props.isStale ? props.handleRefreshVersionedData : processFile}
        dataTestAttrs={props.dataTestAttrs}
      />
    </TooltipWrapper>
  );
}

interface ProcessingAutomationOutputFileButtonProps {
  locked: boolean;
  automationOutputFile: AutomationOutputFileGraphQL;
  runId: string | null;
  configName: string | null;
  updateAutomationOutputFiles: (automationOutputFiles: Array<AutomationOutputFileGraphQL>) => void;
  previousFileNames: Set<string>;
  dataTestAttrs: DataTestAttrs;
}

function ProcessingAutomationOutputFileButton(props: ProcessingAutomationOutputFileButtonProps): JSX.Element {
  const editorPropertiesContext = EditorPropertiesContext.useContext();
  const getProcessingStatistics = async (): Promise<void> => {
    const response = await getProcessingSummaryStatistics(props.automationOutputFile.id);
    if (response) {
      runAutomationOutputFileStatusModal({
        automationFile: {
          ...props.automationOutputFile,
          status: AutomationFileStatusGraphQL.running,
        },
        runId: props.runId,
        configName: props.configName,
        locked: props.locked,
        updateAutomationOutputFiles: props.updateAutomationOutputFiles,
        previousFileNames: props.previousFileNames,
        processingSummaryStatistics: response.processingSummaryStatistics,
        editorPropertiesContext,
      });
    }
  };
  return (
    <Button
      className="processAutomationFileButton"
      buttonStyle={ButtonStyle.TERTIARY_GRAY}
      size={ButtonSize.MINI}
      fullWidth={true}
      text="Processing..."
      icon={<BenchlingIcon icon="refresh" className="glyphicon-spin" />}
      disabled={props.locked}
      onClick={getProcessingStatistics}
      dataTestAttrs={props.dataTestAttrs}
    />
  );
}

/**
 * This component represents an AutomationOutputFile with a PENDING status in a NoteLinkedAssayData.
 */
const UnconnectedPendingOrRunningRunAutomationOutputFileButton: React.FunctionComponent<
  PendingOrRunningRunAutomationOutputFileButtonProps
> = (props: PendingOrRunningRunAutomationOutputFileButtonProps & ConnectedProps): JSX.Element => {
  const setStatusToRunning = (): void => {
    setAutomationFileStatus(props.automationOutputFile.id, AutomationFileStatusGraphQL.running);
  };
  const setStatusToPending = (): void => {
    setAutomationFileStatus(props.automationOutputFile.id, AutomationFileStatusGraphQL.running);
  };
  // Allow overriding react state status. In Connect runs (where we automatically kick off processing)
  // there is a period before the celery task starts processing the file in which the file is still pending
  // which confuses users who expect the file to start processing.
  const isRunning = props.isRunning || props.outputFileStatus === AutomationFileStatusGraphQL.running;

  // Note that `isStale` is only used by the pending button and not the running button.
  // This is because if the live status is RUNNING, the versioned status must be either:
  // RUNNING (meaning data is all in sync)
  // PENDING (this is the expected state after the Process button is clicked, since we don't
  //          update the versioned output file at that point)
  const isStale = props.isStale;

  assert(props.runId != null, 'Unexpected  automation output file with no run');

  if (isRunning) {
    return (
      <ProcessingAutomationOutputFileButton
        locked={props.locked}
        automationOutputFile={props.automationOutputFile}
        runId={props.runId}
        configName={props.configName}
        updateAutomationOutputFiles={props.updateAutomationOutputFiles}
        previousFileNames={props.previousFileNames}
        dataTestAttrs={{
          component: 'PendingOrRunningRunAutomationOutputFileButton',
          element: 'processButton',
        }}
      />
    );
  }
  return (
    <ProcessAutomationOutputFileButton
      locked={props.locked}
      isStale={isStale}
      runId={props.runId}
      fileId={props.fileId}
      automationOutputFile={props.automationOutputFile}
      updateAutomationOutputFiles={props.updateAutomationOutputFiles}
      handleRefreshVersionedData={props.handleRefreshVersionedData}
      setStatusToPending={setStatusToPending}
      setStatusToRunning={setStatusToRunning}
      dataTestAttrs={{
        component: 'PendingOrRunningRunAutomationOutputFileButton',
        element: 'processButton',
      }}
    />
  );
};

export default PendingOrRunningRunAutomationOutputFileButtonConnector.connect(
  UnconnectedPendingOrRunningRunAutomationOutputFileButton
);
